"use client";

import * as React from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

export function DateTimePicker({
  date,
  setDate,
  time,
  loading = false,
  sunday,
  saturday,
  bgClassName,
  mindate,
  dateTimes,
  open,
  keepMeLive,
  onKeepMeLiveChange,
}: {
  date?: Date | null;
  // eslint-disable-next-line no-unused-vars
  setDate: (date: Date) => void;
  time: string;
  loading?: boolean;
  sunday?: boolean;
  saturday?: boolean;
  bgClassName?: string;
  mindate?: string;
  dateTimes?: string[];
  open?: boolean;
  keepMeLive?: string | null;
  // eslint-disable-next-line no-unused-vars
  onKeepMeLiveChange?: (value: string) => void;
}) {
  const dayOfWeek = [];
  if (!sunday) dayOfWeek.push(0);
  if (!saturday) dayOfWeek.push(6);
  const disabledDays: any = [{ after: new Date() }, { dayOfWeek }];
  if (mindate) {
    disabledDays.unshift({ before: dayjs(mindate).toDate() });
  }

  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [delayedOpen, setDelayedOpen] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setDelayedOpen(true);
      }, 0);
      return () => clearTimeout(timer);
    } else {
      setDelayedOpen(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!scrollAreaRef.current || !time || !open) {
      return;
    }
    const timeElement = scrollAreaRef.current.querySelector(
      `button[id="${time}"]`
    );
    if (timeElement) {
      timeElement.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "start",
      });
    }
  }, [scrollAreaRef, delayedOpen, loading, dateTimes]);

  return (
    <Tooltip>
      <PopoverTrigger asChild>
        <TooltipTrigger asChild>
          <Button
            variant={"link"}
            className={cn(
              "justify-start text-left font-normal px-2",
              !date && ""
            )}
          >
            <CalendarIcon className="hidden md:inline-flex mr-2 h-4 w-4" />
            {loading ? (
              <Skeleton className="w-[150px] h-[20px] bg-white/20 rounded-full justify-center items-center" />
            ) : date ? (
              dayjs(date).format("MMM Do YYYY [at] h:mm A")
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </TooltipTrigger>
      </PopoverTrigger>

      <PopoverContent
        align="center"
        side="bottom"
        className={cn("w-auto block mt-1 p-0 rounded-t-none", bgClassName)}
      >
        <div className="flex flex-row h-[350px]">
          <Calendar
            mode="single"
            className="border-r border-b-none border-top-none"
            selected={date || undefined}
            onSelect={(value) => {
              // Add the date to the current time
              const newDate = dayjs(value)
                .set("hour", dayjs(time, "HHmm").hour())
                .set("minute", dayjs(time, "HHmm").minute())
                .toDate();
              setDate(newDate);
            }}
            initialFocus
            disabled={disabledDays}
            defaultMonth={
              date && dayjs(date).isValid() ? dayjs(date).toDate() : new Date()
            }
          />

          <ScrollArea
            type="always"
            viewportRef={scrollAreaRef}
            className="h-full"
          >
            <div className="flex flex-col p-1">
              <div className="flex flex-col space-y-2">
                {dateTimes?.length
                  ? dateTimes.map((value) => {
                      return (
                        <button
                          key={value}
                          id={value}
                          onClick={() => {
                            // Combine the current date, with the selected time
                            const newDate = dayjs(date)
                              .set("hour", dayjs(value, "HHmm").hour())
                              .set("minute", dayjs(value, "HHmm").minute())
                              .toDate();
                            setDate(newDate);
                            // setTime(dayjs(time, "HHmm").toDate());
                          }}
                          className={cn(
                            "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                            time === value &&
                              "bg-white text-black hover:bg-white hover:text-black focus:bg-white focus:text-black"
                          )}
                        >
                          {dayjs(value, "HHmm").format("h:mm A")}
                        </button>
                      );
                    })
                  : null}
              </div>
            </div>
          </ScrollArea>
        </div>

        <div className="flex justify-between px-2 py-1 border-t">
          <span className="flex flex-row items-center">
            <Button
              onClick={() => {
                const value = keepMeLive === "true" ? "false" : "true";
                localStorage.setItem("camsKeepMeLive", value);
                console.log({ value });
                if (onKeepMeLiveChange) {
                  onKeepMeLiveChange(value);
                }
              }}
              variant="ghost"
              size="sm"
              className="group"
            >
              <span className="h-5 w-5 flex flex-col justify-center border-white/30 items-center border rounded mr-1.5 group-hover:border-white/50">
                {keepMeLive === "true" ? (
                  <CheckIcon className="h-3 w-3" />
                ) : null}
              </span>
              Keep Me Live
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button className="p-0.5 h-6 w-6" size="icon" variant="ghost">
                  <InformationCircleIcon className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="max-w-[350px]">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Automatically stay up to date with the latest image from
                    this camera. When a new image becomes available we will show
                    a message in the bottom right of your screen and you will be
                    taken to the latest image.
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </span>

          <PopoverClose asChild>
            <Button variant="ghost" size="sm">
              Close
            </Button>
          </PopoverClose>
        </div>
      </PopoverContent>

      <TooltipContent side="bottom">
        <p>Change Date or Time</p>
      </TooltipContent>
    </Tooltip>
  );
}
